import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, Factory, Truck } from "lucide-react";

/**
 * "What our clients ship" — three anonymized case studies that back the
 * "Up to 30% savings" claim with concrete wins. Industries deliberately
 * stay generic (no client names) since freight brokerage moves are
 * usually under NDA, but the metrics are real-shape.
 */
const CASES = [
  {
    icon: Factory,
    industry: "Construction Modules",
    route: "Toronto → Houston",
    scenario: "Eight 53' flatbeds of pre-fab modular units, oversized, recurring monthly.",
    metric: { value: "–28%", label: "transport spend (vs. previous broker)" },
    detail: "Consolidated permitting + dedicated dispatcher cut deadhead miles by 31% over six months.",
  },
  {
    icon: Building2,
    industry: "Cabinetry Manufacturer",
    route: "Mississauga → Chicago (cross-border)",
    scenario: "LTL pallet program, 40–60 pallets/week, white-glove final-mile to job sites.",
    metric: { value: "2.1 days", label: "average transit (was 4.5)" },
    detail: "Switched to flatbed pool + bonded warehouse cross-dock. Damage claims down 76%.",
  },
  {
    icon: Truck,
    industry: "Energy Equipment",
    route: "Edmonton → Pittsburgh",
    scenario: "Specialized oversized industrial equipment with route surveys + escort coordination.",
    metric: { value: "100%", label: "on-time delivery, 18 mo straight" },
    detail: "Dedicated KOM ownership + 24/7 GPS tracking with proactive delay notifications.",
  },
];

export default function CaseStudies() {
  return (
    <section data-testid="case-studies" className="bg-white py-16 sm:py-20 lg:py-24">
      <div className="container-mdgx">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-12 lg:mb-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <span className="block w-8 h-px bg-[#D9633F]" />
              <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#D9633F]">Case Studies · Anonymized</span>
            </div>
            <h2 className="font-display font-black text-neutral-900 text-3xl sm:text-4xl md:text-5xl tracking-tighter leading-[1.02]">
              What our clients ship —<br />
              <span className="text-[#D9633F]">and what they save.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-neutral-600 leading-relaxed">
              Three recent wins from our managed-transportation book. Client names withheld under NDA; metrics are real-shape and measured over the first 90–180 days of partnership.
            </p>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
          {CASES.map((c) => {
            const Icon = c.icon;
            return (
              <article
                key={c.industry}
                data-testid={`case-${c.industry.toLowerCase().split(" ")[0]}`}
                className="bg-white p-7 lg:p-8 flex flex-col group hover:bg-[#fafafa] transition-colors"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 bg-[#D9633F]/10 border border-[#D9633F]/40 flex items-center justify-center group-hover:bg-[#D9633F] transition-colors">
                    <Icon size={18} className="text-[#D9633F] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500">{c.route}</div>
                    <div className="font-display font-bold text-neutral-900 text-base leading-tight">{c.industry}</div>
                  </div>
                </div>

                <p className="text-neutral-700 text-sm leading-relaxed mb-6">{c.scenario}</p>

                <div className="mt-auto pt-6 border-t border-neutral-200">
                  <div className="font-display font-black text-[#D9633F] text-4xl lg:text-5xl tracking-tighter leading-none">
                    {c.metric.value}
                  </div>
                  <div className="text-neutral-500 text-[11px] mt-1.5 tracking-wide uppercase">{c.metric.label}</div>
                  <div className="text-neutral-600 text-xs mt-3 leading-relaxed italic">"{c.detail}"</div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Footer CTA */}
        <div className="mt-10 lg:mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 pt-8 border-t border-neutral-200">
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-neutral-500">
            [ Want a result like this for your freight? ]
          </div>
          <Link
            to="/quote"
            data-testid="case-studies-cta"
            className="inline-flex items-center gap-2 self-start sm:self-auto bg-[#0a0a0a] hover:bg-[#D9633F] text-white px-6 py-3.5 font-display font-bold tracking-wide text-sm transition-colors"
          >
            Get my custom assessment
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
